package components

import androidx.compose.runtime.Composable
import frontend.lib.classList
import kotlinx.browser.document
import kotlinx.datetime.*

import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import wrappers.DateRangePicker
import wrappers.DatepickerOptions

fun DatepickerOptions(): DatepickerOptions = js("{}")

class DatepickerAttrsBuilder {
    var onChange: (value: Instant?) -> Unit = {}
    var format: String = "yyyy-mm-dd"
    var value: Instant? = null

    fun onChangeInput(date: String) {
        if (date == "") return onChange(null)
        val ld = LocalDate.parse(date)
        val zone = TimeZone.currentSystemDefault()
        val ldt = ld.atStartOfDayIn(zone).toLocalDateTime(zone)
        val instant = ldt.toInstant(zone)
        onChange(instant)
    }

    fun onChange(handler: (value: Instant?) -> Unit) {
        onChange = handler
    }

    fun format(f: String) {
        format = f
    }

    fun value(dt: Instant?) {
        value = dt
    }

    fun valueString(): String {
        if (value == null) return ""
        else {
            val zone = TimeZone.currentSystemDefault()
            var dateString = ""
            value?.let {
                val ldt = it.toLocalDateTime(zone)
                val ld = LocalDate(ldt.year, ldt.monthNumber, ldt.dayOfMonth)
                dateString = ld.toString()
            }

            return dateString
        }
    }
}

class DatepickerRangeAttrsBuilder {
    var from = DatepickerAttrsBuilder()
    var to = DatepickerAttrsBuilder()
    var format: String = "yyyy-mm-dd"

    fun From(attrs: DatepickerAttrsBuilder.() -> Unit = {}) {
        attrs.invoke(from)
    }

    fun To(attrs: DatepickerAttrsBuilder.() -> Unit = {}) {
        attrs.invoke(to)
    }

    fun format(f: String) {
        format = f

        from.format(f)
        to.format(f)
    }
}

@Composable
fun DatepickerRange(attrs: DatepickerRangeAttrsBuilder.() -> Unit) {
    val builder = DatepickerRangeAttrsBuilder()
    attrs.invoke(builder)

    Div(attrs = {
        attr("date-rangepicker", "true")
        classList("flex items-center")
        ref {
            DateRangePicker(it, DatepickerOptions().apply {
                autohide = true
                format = builder.format
            })
            onDispose {
                document.getElementsByClassName("datepicker-dropdown")?.let {
                    it.asList().forEach { it.remove() }
                }
            }
        }
    }) {
        Div(attrs = { classList("relative") }) {
            Div(attrs = { classList("flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none") }) {
                Svg(attrs = {
                    classList("w-5 h-5 text-gray-500 dark:text-gray-400")
                    attr("fill", "currentColor")
                    attr("viewBox", "0 0 20 20")
                    attr("xmlns", "http://www.w3.org/2000/svg")
                }) {
                    Path(attrs = {
                        attr("fill-rule", "evenodd")
                        attr("clip-rule", "evenodd")
                        attr("d", "M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z")
                    })
                }
            }

            Input(InputType.Text) {
                value(builder.from.valueString())
                name("start")
                classList("bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500")
                placeholder("Select date from")
//                disabled()

                ref {
                    fun onChangeDate(e: Event) { builder.from.onChangeInput(it.value) }
                    it.addEventListener("changeDate", { onChangeDate(it) })
                    onDispose { it.removeEventListener("changeDate", { onChangeDate(it) }) }
                }
            }
        }

        Span(attrs = { classList("mx-4 text-gray-500") }) { Text("to") }

        Div(attrs = { classList("relative") }) {
            Div(attrs = { classList("flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none") }) {
                Svg(attrs = {
                    classList("w-5 h-5 text-gray-500 dark:text-gray-400")
                    attr("fill", "currentColor")
                    attr("viewBox", "0 0 20 20")
                    attr("xmlns", "http://www.w3.org/2000/svg")
                }) {
                    Path(attrs = {
                        attr("fill-rule", "evenodd")
                        attr("clip-rule", "evenodd")
                        attr("d", "M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z")
                    })
                }
            }

            Input(InputType.Text) {
                value(builder.to.valueString())
                name("end")
                classList("bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500")
                placeholder("Select date to")
//                disabled()

                ref {
                    fun onChangeDate(e: Event) { builder.to.onChangeInput(it.value) }
                    it.addEventListener("changeDate", { onChangeDate(it) })
                    onDispose { it.removeEventListener("changeDate", { onChangeDate(it) }) }
                }
            }
        }
    }
}